import { checkClaimsWeb } from "@eventr/common";
import type {
  AnyComponentModel,
  AnyComponentSpec,
  PushDirection,
  StackModel,
  StackSpec,
  TabSpec,
  ViewSpec,
} from "@eventr/native/navigation";
import { NativeNavigation } from "@eventr/native/navigation";
import { pendingAuthLink, subscribePendingAuthLink } from "feat/deeplink/auth-deeplink";
import { pendingGuestLink, subscribePendingGuestLink } from "feat/guest/pendingGuestLink";
import {
  getTabDisabled,
  isPathTabDisabled,
  isTabDisabled,
  reportTabPress,
  useTabDisabledStore,
} from "feat/layout/tab-bar/tabDisabled.store";
import {
  presentedTabs,
  TAB_BY_ID,
  TABS,
  type TabAvailability,
  type TabDefinition,
  type TabId,
} from "feat/layout/tab-bar/tabs";
import { readPaneSplit, writePaneSplit } from "feat/layout/panes/pane-split";
import { tabOfStack } from "feat/layout/tab-bar/tab-stack-ids";
import { PANE_GROUP_IDS, resolveScreenLayout } from "feat/layout/screen-layout";
import { useTwoPaneSettingStore } from "feat/layout/two-pane-setting.store";
import i18n from "i18n/i18next";
import { navbarTranslation } from "i18n/translateHelpers";
import { useHasFeature } from "hooks/useHasFeature";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { matchPath } from "react-router";
import { allRouteNodes } from "routing/nodes";
import { pathnameOf } from "routing/route";
import { shallow } from "zustand/shallow";
import { useBoundStore } from "../../state/bound.store";
import { useHomeTabStore } from "../../state/itineraryState/homeTab.store";
import { getTranslationKeyFromHomeTab } from "../../state/itineraryState/homeTabs";
import { selectHasTeams } from "../../state/hooks/useHasTeams";
import { useGuestStore } from "../guest/guest.store";
import { isModalPresented, nativeNavigationReact } from "./init";
import { TRAILING_ROOT_STATE_KEY } from "./modalRoot";
import { nativeInterfaceStyle, nativeTabBarTint } from "./nativeTheme";
import { isIosNativeNavigation } from "./platform";
import {
  AUTH_ROOT_PATH,
  AUTH_STACK_ID,
  authStackViews,
  isAuthRootHeld,
  isRootSwapInFlight,
  POP_TO_ROOT_COUNT,
  presentedNativeRoot,
  type RootKind,
  setPresentedNativeRoot,
  setRootSwapInFlight,
  setTabReflowInFlight,
  subscribeNativeRootState,
} from "./root-state";
import { listenForVerticalBarChanges, refreshVerticalBar } from "./vertical-bar";
import {
  isTrailingStackId,
  leadingStackIdOf,
  listenForSplitChanges,
  refreshSplitCollapsed,
  splitComponentId,
  trailingStackId,
  useSplitStore,
} from "./split.store";
import { loadTabIcon } from "./tabIcons";
import { ensureToolbarIcon, getToolbarIcon, TOOLBAR_ICON_SCALE } from "./toolbarIcons";

// Re-exported for existing importers.
export { AUTH_ROOT_PATH, AUTH_STACK_ID, isRootSwapInFlight, POP_TO_ROOT_COUNT, presentedNativeRoot };

/**
 * The componentId of the presented tabs root, so `selectTab` can target it. Only set
 * while the tabs root is presented; undefined pre-auth or before the first present.
 */
let tabsComponentId: string | undefined;

/** Whether the presented bar's pane tabs are native splits. Lags a rebuild like `presentedTabIds`. */
let presentedSplit = false;

function setPresentedSplit(split: boolean) {
  presentedSplit = split;
  useSplitStore.getState().setTabsAreSplits(split);
}

/** Whether `buildTabsSpec` last built them as splits, which `reconcile` promotes once presented. */
let builtSplit = false;

/** Whether the presented bar has the Map tab. Lags a rebuild like `presentedTabIds`. */
let presentedMapTab = false;

/** Whether `buildTabsSpec` last built the Map tab, which `reconcile` promotes once presented. */
let builtMapTab = false;

/**
 * The native root for `hasSession`. "tabs" when signed in or guest with no pending auth or
 * guest link, otherwise "auth". iOS only, Android and web use BrowserRouter.
 */
function rootFor(hasSession: boolean): RootKind {
  return hasSession && !pendingAuthLink() && !pendingGuestLink() && !isAuthRootHeld() ? "tabs" : "auth";
}

/** Fires `listener` when either the pending auth link or the pending guest link changes. */
function subscribePendingLinks(listener: () => void): () => void {
  const unsubscribeAuth = subscribePendingAuthLink(listener);
  const unsubscribeGuest = subscribePendingGuestLink(listener);
  return () => {
    unsubscribeAuth();
    unsubscribeGuest();
  };
}

/** Snapshot for the subscription above. Changes when either pending link is set or cleared. */
function pendingLinksSnapshot(): string {
  return `${pendingAuthLink() !== null}|${pendingGuestLink() !== null}`;
}

/**
 * The root the current auth state calls for, which between a token arriving and the swap
 * completing is not the one on screen. Navigation reads it to know the tabs are coming.
 */
export function desiredNativeRoot(): RootKind {
  return rootFor(useBoundStore.getState().IDtoken !== undefined || useGuestStore.getState().isGuestMode);
}

/**
 * Switch the bottom tab bar to the tab whose root path is `path`, without pushing a
 * new view onto whichever tab is currently active. Used when a screen in one tab
 * (e.g. Account) needs to land the user back on another tab's existing stack (e.g.
 * Home) instead of pushing a duplicate root onto its own stack.
 * A tab the bar is not showing is a no-op.
 * No-op on Android/web, and before the tabs root has been presented.
 */
export async function selectTab(tab: TabId): Promise<void> {
  if (!isIosNativeNavigation() || !tabsComponentId) return;
  if (isTabDisabled(tab)) return;
  // `isTabOnBar`, not the flags: mid-reflow the flags already describe the set being
  // installed, and selecting a stack the bar has not installed yet addresses a component
  // that is still being created.
  if (!isTabOnBar(tab)) return;
  const stackId = TAB_BY_ID[tab]?.stackId;
  if (!stackId) return;
  // The bar knows a tab by its component: the split, for a pane tab built as one.
  const selectedId = isSplitTab(tab, presentedSplit) ? splitComponentId(stackId) : stackId;
  await NativeNavigation.update({ id: tabsComponentId, update: { selectedId } });
}

/** Takes a pushed view back off `stackId`. Home's is skipped: `resetToTabRoot` empties it. */
export async function dismissPushedView(stackId: string | undefined): Promise<void> {
  if (!isIosNativeNavigation() || !stackId || stackId === TAB_BY_ID.home.stackId) return;
  await NativeNavigation.pop({ stack: stackId, count: 1, animated: false }).catch(() => {});
}

/** Whether `stackId` names the stack on screen. Every tab's view runs the same React tree. */
export async function isFrontmostStack(stackId: string | undefined): Promise<boolean> {
  if (!stackId) return false;
  try {
    const { stack } = await NativeNavigation.get();
    // An expanded split reports its trailing column, which is the same tab on screen.
    return stack?.id === stackId || stack?.id === trailingStackId(stackId);
  } catch {
    return false;
  }
}

/**
 * Switch to the tab rooted at `tabPath`, then push a new view onto that tab's own
 * stack. Used when a screen reached from one tab (e.g. the shared home/chat/team
 * header) needs to land on a screen that lives inside another tab (e.g. Account),
 * rather than pushing it onto the current tab's stack (which would leave the tab bar
 * on the wrong tab and orphan the pushed view there).
 * No-op on Android/web and before the tabs root has been presented; callers should
 * fall back to a plain `pushPath` in that case.
 * Pops the target stack back to its root first, so repeat taps (e.g. re-opening the
 * same header link from a different origin tab) replace the previous push instead of
 * stacking a fresh layer on top of it every time.
 * Returns whether it pushed.
 */
export async function pushInTab(
  tabName: TabId,
  viewPath: string,
  title: string,
  direction?: PushDirection,
): Promise<boolean> {
  if (!isIosNativeNavigation() || !tabsComponentId) return false;
  if (isTabDisabled(tabName) || isPathTabDisabled(viewPath)) return false;
  const tab = TAB_BY_ID[tabName];
  if (!tab) return false;
  // A tab the bar is not presenting has no stack to push onto.
  if (!isTabOnBar(tabName)) return false;
  // Select the target tab first and let it become frontmost before mutating its
  // stack. Popping/pushing a backgrounded tab's stack races the tab-switch animation
  // and can drop the push.
  await selectTab(tabName);
  await NativeNavigation.pop({ stack: tab.stackId, count: POP_TO_ROOT_COUNT, animated: false });
  // An entry of a split tab's list opens in its trailing column, replacing what was there, in
  // place beside the list; collapsed it covers the list, so it slides like any push.
  const trails = isSplitTab(tabName, presentedSplit) && trailsInTab(tabName, viewPath);
  await NativeNavigation.push({
    target: trails ? trailingStackId(tab.stackId) : tab.stackId,
    ...(trails && { mode: "root" as const }),
    ...(trails && !useSplitStore.getState().collapsed && { animated: false }),
    component: {
      type: "view",
      path: viewPath,
      title,
      ...(trails && { state: { [TRAILING_ROOT_STATE_KEY]: true } }),
    },
    direction,
  });
  return true;
}

/**
 * Switch to `tabName` and take its stack back to the tab's own root view. `pushInTab` cannot
 * express this: the root view is already at the bottom of the stack, so pushing its path
 * again stacks a second copy of it.
 *
 * Returns false when there is no native tabs root to drive, so the caller can fall back to
 * an ordinary navigation.
 */
export async function resetToTabRoot(tabName: TabId): Promise<boolean> {
  if (!isIosNativeNavigation() || !tabsComponentId) return false;
  const tab = TAB_BY_ID[tabName];
  if (!tab) return false;
  // Neither a disabled nor an absent tab has a stack to pop.
  if (isTabDisabled(tabName) || !isTabOnBar(tabName)) return false;
  // Select first: mutating a backgrounded tab's stack races the tab-switch animation.
  await selectTab(tabName);
  await NativeNavigation.pop({ stack: tab.stackId, count: POP_TO_ROOT_COUNT, animated: false });
  // And the trailing column back to what it shows before an entry is chosen. Expanded only:
  // collapsed, a push to the trailing column nests over the list, so this covered Home with its
  // headerless Summary column after a team swap. The pop above has already taken off anything
  // nested, and the plugin resets the column behind it (see `resetItineraryTrailingColumns`).
  if (isSplitTab(tabName, presentedSplit) && !useSplitStore.getState().collapsed) {
    await NativeNavigation.push({
      target: trailingStackId(tab.stackId),
      mode: "root",
      animated: false,
      component: trailingRootView(tabName),
    }).catch(() => {});
  }
  return true;
}

/** The split tabs whose trailing column shows something of the selected itinerary. */
const ITINERARY_TRAILING_TABS: readonly TabId[] = ["home", "team", "chat"];

/**
 * Puts each itinerary tab's trailing column back to what it shows before an entry is chosen. An
 * entry, member or room left open there belongs to the itinerary that was selected when it opened,
 * and swapping itinerary otherwise left it on screen beside the new one's list. Account's column
 * is left alone: nothing in it is the itinerary's, and the itinerary list itself can be there.
 *
 * Collapsed (an iPhone), a push to the trailing column nests over the list, so pushing its first
 * screen covered Home with an empty column on every swap. There the tab goes back to its list
 * instead, taking off the nested entry and anything opened from the old itinerary, as
 * `goBackInTrailingColumn` takes an entry off, and the plugin resets the column behind it.
 */
async function resetItineraryTrailingColumns(): Promise<void> {
  if (!isIosNativeNavigation() || !tabsComponentId) return;
  const collapsed = useSplitStore.getState().collapsed;
  await Promise.all(
    ITINERARY_TRAILING_TABS.filter(
      (tabName) => isSplitTab(tabName, presentedSplit) && isTabOnBar(tabName) && !isTabDisabled(tabName),
    ).map((tabName) => {
      const tab = TAB_BY_ID[tabName];
      if (!tab) return undefined;
      if (collapsed) {
        return NativeNavigation.pop({ stack: tab.stackId, count: POP_TO_ROOT_COUNT, animated: false }).catch(() => {});
      }
      return NativeNavigation.push({
        target: trailingStackId(tab.stackId),
        mode: "root",
        animated: false,
        component: trailingRootView(tabName),
      }).catch(() => {});
    }),
  );
}

/**
 * Goes back from a screen in a pane tab's trailing column, `stackId`: one screen back in the
 * column, or, from its first screen, closes the entry the way the web trailing pane's Close does.
 * Expanded the column shows what it does before an entry is chosen; collapsed, the entry nested
 * over the list comes off, and the plugin resets the column behind it.
 */
export async function goBackInTrailingColumn(stackId: string): Promise<void> {
  const { count } = await NativeNavigation.pop({ stack: stackId, count: 1 });
  if (count > 0) return;
  const tabName = tabOfStack(leadingStackIdOf(stackId));
  const tab = tabName && TAB_BY_ID[tabName];
  if (!tab || !isSplitTab(tab.id, presentedSplit)) return;
  if (useSplitStore.getState().collapsed) {
    await NativeNavigation.pop({ stack: tab.stackId, count: 1 });
    return;
  }
  // In place, as the entry appeared.
  await NativeNavigation.push({
    target: stackId,
    mode: "root",
    animated: false,
    component: trailingRootView(tab.id),
  });
}

/** Whether `tab` is built as a split when `split` says the bar is built with them. */
function isSplitTab(tab: TabId, split: boolean): boolean {
  return split && (PANE_GROUP_IDS as readonly string[]).includes(tab);
}

/** Whether `path` is an entry of `tab`'s list, which opens in the trailing pane. */
function trailsInTab(tab: TabId, path: string): boolean {
  const layout = resolveScreenLayout({
    width: 0,
    pathname: pathnameOf(path),
    activeTab: tab,
    twoPaneSetting: true,
    iosNativeNavigation: true,
    splitCollapsed: false,
  });
  return layout.layout === "two-pane" && layout.trailingPane === "route" && layout.leadingPane === tab;
}

/** The first screen of a pane tab's trailing column. */
function trailingRootView(tab: TabId): ViewSpec {
  return { type: "view", path: `/panes/${tab}`, title: "", stackItem: { bar: { visible: false } } };
}

/** Whether pane tabs are built as native splits now, which the two-pane setting decides. */
function wantsSplitTabs(): boolean {
  return isIosNativeNavigation() && useTwoPaneSettingStore.getState().twoPaneSetting;
}

/** Carries a refused tab's selection back through the plugin's click event. */
const TAB_SELECT_BUTTON_PREFIX = "tab-select:";

const tabSelectButtonId = (id: TabId) => `${TAB_SELECT_BUTTON_PREFIX}${id}`;

const tabIdFromButtonId = (buttonId: string): TabId | undefined => {
  if (!buttonId.startsWith(TAB_SELECT_BUTTON_PREFIX)) return undefined;
  const id = buttonId.slice(TAB_SELECT_BUTTON_PREFIX.length);
  return TABS.find((tab) => tab.id === id)?.id;
};

/**
 * The Map tab, after Home while the pane tabs are splits showing two panes, as on the web's
 * two-pane bars. Collapsed to one pane (an iPad zoomed, a folded Duo) the bar keeps its four
 * tabs and Home's own header switches to the map. It is no tab of its own: a tap is refused and opens Home on its map instead, so Home stays highlighted.
 * Its view is the trailing-root route with no pane tab named, which draws nothing.
 */
const MAP_TAB_STACK_ID = "map-tab-stack";
const MAP_TAB_SELECT_BUTTON_ID = `${TAB_SELECT_BUTTON_PREFIX}map`;

/** How long the splits stay collapsed or expanded before the bar follows them. */
const SPLIT_SETTLE_MS = 500;

/** Whether the bar gets the Map tab beside `ids`: Home has a map to switch to. */
function wantsMapTab(ids: readonly TabId[], split: boolean): boolean {
  return split && !useSplitStore.getState().collapsed && ids.includes("home");
}

/** The Map tab's current title, image and refused selection. */
async function mapTabAppearance() {
  await ensureToolbarIcon("Map");
  const uri = getToolbarIcon("Map");
  return {
    title: i18n.t(getTranslationKeyFromHomeTab("map")),
    image: uri ? { uri, scale: TOOLBAR_ICON_SCALE } : undefined,
    selectButtonId: MAP_TAB_SELECT_BUTTON_ID,
  };
}

async function buildMapTabSpec(): Promise<TabSpec> {
  const { title, image, selectButtonId } = await mapTabAppearance();
  return {
    title,
    ...(image ? { image } : {}),
    selectButtonId,
    component: {
      type: "stack",
      id: MAP_TAB_STACK_ID,
      bar: { visible: false },
      components: [{ type: "view", path: "/panes/map", title, stackItem: { bar: { visible: false } } }],
    },
  };
}

/** Every tab's spec, with the Map tab after Home when `mapTab` says so. */
async function buildTabSpecs(tabs: readonly TabDefinition[], split: boolean, mapTab: boolean): Promise<TabSpec[]> {
  const specs = await Promise.all(tabs.map((tab) => buildTabSpec(tab, split)));
  if (mapTab) specs.splice(tabs.findIndex((tab) => tab.id === "home") + 1, 0, await buildMapTabSpec());
  return specs;
}

/** A Map tab tap: Home at its root, showing the map, as the web's Map tab opens it. */
function openHomeMap() {
  if (isTabDisabled("home")) {
    reportTabPress("home");
    return;
  }
  useHomeTabStore.getState().setHomeTab("map");
  void resetToTabRoot("home");
}

/**
 * Chat's label mirrors `ChatNavBarItem`'s web item: "Chat" while the org's claims allow
 * `sendMessage`, "Messages" for a read-only member. Every other tab keeps its own label.
 */
function tabLabel(tab: TabDefinition): string {
  if (tab.id !== "chat") return tab.label;
  return checkClaimsWeb(["/"], ["sendMessage"]) ? tab.label : "Messages";
}

/**
 * A tab's current title, image and selection behaviour. Titles resolve here: the tabs
 * module is evaluated before i18n initialises.
 */
async function tabAppearance(tab: TabDefinition) {
  const disabled = getTabDisabled(tab.id);
  const summary = !disabled && tab.id === "home" && homeTapsRefused();
  if (tab.id === "home") homeSummaryStated = summary;
  return {
    title: navbarTranslation(tabLabel(tab)),
    image: await loadTabIcon(disabled?.nativeSvg ?? tab.nativeSvg),
    selectButtonId: disabled
      ? tabSelectButtonId(tab.id)
      : summary
        ? HOME_SUMMARY_SELECT_BUTTON_ID
        : tab.id === "account"
          ? ACCOUNT_ROOT_SELECT_BUTTON_ID
          : null,
  };
}

/**
 * The Account tab always opens at its root: a tap is refused and reopens Account with its stack
 * popped, rather than on whatever was last pushed there.
 */
const ACCOUNT_ROOT_SELECT_BUTTON_ID = `${TAB_SELECT_BUTTON_PREFIX}account-root`;

/** Home's tab while its split shows two panes. A tap on Home, even selected, is refused, for `openHomeSummary`. */
const HOME_SUMMARY_SELECT_BUTTON_ID = `${TAB_SELECT_BUTTON_PREFIX}home-summary`;

/** Whether Home's tab was last given `HOME_SUMMARY_SELECT_BUTTON_ID`. */
let homeSummaryStated = false;

function homeTabShowsMapBeside(): boolean {
  return presentedMapTab && !useSplitStore.getState().collapsed && useHomeTabStore.getState().homeTab === "map";
}

/** Whether Home's tab hands its taps to `openHomeSummary`: its split shows two panes. */
function homeTapsRefused(): boolean {
  return isSplitTab("home", presentedSplit) && !useSplitStore.getState().collapsed;
}

/**
 * A Home tab tap while its split shows two panes. With the map beside it, Summary comes back, as
 * with the Map tab on the bar Home's header has no tabs of its own in two panes. Otherwise, with
 * Home already selected, Home goes back to its list with its own content beside it.
 */
async function openHomeSummary() {
  if (homeTabShowsMapBeside()) {
    useHomeTabStore.getState().setHomeTab("summary");
    await selectTab("home");
    return;
  }
  if (await isFrontmostStack(TAB_BY_ID.home.stackId)) await resetToTabRoot("home");
  else await selectTab("home");
}

async function buildTabSpec(tab: TabDefinition, split: boolean): Promise<TabSpec> {
  const { title, image, selectButtonId } = await tabAppearance(tab);
  const paneSplit = readPaneSplit();
  const stack = {
    type: "stack" as const,
    id: tab.stackId,
    // The stack bar is visible so pushed screens get a native back button, title and
    // swipe-back; the root view hides it per `hidesRootBar`. Home / Chat / Team are not
    // registry-migrated, so a pushed screen drawing its own in-app header needs
    // NavigationView's defer-to-native treatment to avoid a double header. No bar
    // colours: light and dark come from setInterfaceStyle.
    bar: { visible: true },
    components: [
      {
        type: "view" as const,
        path: tab.route,
        title,
        stackItem: { bar: { visible: !tab.hidesRootBar } },
      },
    ],
  } satisfies StackSpec;
  return {
    title,
    image,
    // Omitted rather than null: null is the update path's way of clearing an id.
    ...(selectButtonId ? { selectButtonId } : {}),
    // A pane tab is a split, leading with the tab's own stack (same id, so everything that
    // addresses the tab's stack keeps working) and trailing with what its entries open in.
    // UIKit collapses it to one column in a compact width.
    component: isSplitTab(tab.id, split)
      ? {
          type: "split" as const,
          id: splitComponentId(tab.stackId),
          // Where the divider was last dragged, on iOS or in the web layout.
          ...(paneSplit !== null && { primaryFraction: paneSplit }),
          primary: stack,
          secondary: {
            type: "stack" as const,
            id: trailingStackId(tab.stackId),
            bar: { visible: true },
            components: [trailingRootView(tab.id)],
          },
        }
      : stack,
  };
}

/** Tab availability, read outside React. */
function tabAvailability(): TabAvailability {
  const state = useBoundStore.getState();
  return {
    chatEnabled: state.chatEnabled,
    teamPageEnabled: state.teamPageEnabled,
    hasTeams: selectHasTeams(state),
    isGuestMode: useGuestStore.getState().isGuestMode,
  };
}

/**
 * The tabs the bar is showing, as confirmed by a `present` or by a reflow the native side
 * accepted. Never the set a call is about to install. A reflow that fails or is superseded
 * would otherwise record a set the bar never took, and the tab set is only reconsidered
 * when a flag moves, so no second transition would come along to correct it.
 */
let presentedTabIds: TabId[] = [];

/** The set `buildTabsSpec` last built, which `reconcile` promotes once `present` resolves. */
let builtTabIds: TabId[] = [];

/**
 * Whether the bar is showing `tabName` right now, rather than whether the flags say it
 * should be. The two diverge for the length of a reflow — `presentedTabIds` deliberately
 * lags until the native side has accepted the new set — so anything addressing a tab's
 * stack has to ask this. Asking the flags instead sends `selectTab`, `pop` and `push` at a
 * stack that is mid-teardown or not yet created, which breaks the transition.
 */
export function isTabOnBar(tabName: TabId): boolean {
  return presentedTabIds.includes(tabName);
}

const PORTRAIT_QUERY = "(orientation: portrait)";

/** Held upright the bar stays whole, so every tab is always in reach; held wide it minimizes on scroll. */
const minimizesOnScroll = () => !window.matchMedia(PORTRAIT_QUERY).matches;

async function buildTabsSpec(): Promise<AnyComponentSpec> {
  const tabs = presentedTabs(tabAvailability());
  builtTabIds = tabs.map((tab) => tab.id);
  builtSplit = wantsSplitTabs();
  builtMapTab = wantsMapTab(builtTabIds, builtSplit);
  // Where the window is wide enough, as on iPad, the tabs adapt to a sidebar; held wide, the bar
  // collapses to the current tab while a page scrolls down (see `minimizesOnScroll`).
  return {
    type: "tabs",
    sidebar: true,
    minimizeOnScroll: minimizesOnScroll(),
    // In a vertical bar (iPhone Duo held wide) a screen's own buttons give way before the tabs do,
    // so every tab stays in reach.
    compressesTabBarFirst: false,
    tabs: await buildTabSpecs(tabs, builtSplit, builtMapTab),
  };
}

const sameTabs = (a: readonly TabId[], b: readonly TabId[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

/**
 * The pre-auth stack, every view in one `present()`. A guest link that fails while signed out
 * resets to Welcome, which only works because Welcome is under its spinner.
 */
function buildAuthSpec(): AnyComponentSpec {
  const components: ViewSpec[] = authStackViews(pendingAuthLink()?.path, pendingGuestLink() ?? undefined).map(
    ({ path, barVisible }) => ({ type: "view", path, title: "", stackItem: { bar: { visible: barVisible } } }),
  );
  return { type: "stack", id: AUTH_STACK_ID, bar: { visible: true }, components };
}

/**
 * Pushes the current title, image and selection behaviour of every presented tab, each
 * addressed by its stack id. All three go together: the native side drops an update
 * carrying none of them.
 */
export async function syncTabs(): Promise<void> {
  if (!isIosNativeNavigation() || !tabsComponentId) return;
  await Promise.all(
    // The tabs the bar has, not the ones the flags call for: restating the appearance of a
    // stack that is not installed is addressed at a component native does not have.
    presentedTabIds.map(async (id) => {
      const tab = TAB_BY_ID[id];
      if (!tab) return;
      const { title, image, selectButtonId } = await tabAppearance(tab);
      await NativeNavigation.update({
        id: tab.stackId,
        update: { title, image, selectButtonId },
      }).catch(() => {});
    }),
  );
  if (presentedMapTab) {
    const { title, image, selectButtonId } = await mapTabAppearance();
    await NativeNavigation.update({
      id: MAP_TAB_STACK_ID,
      update: { title, ...(image ? { image } : {}), selectButtonId },
    }).catch(() => {});
  }
}

/**
 * Set by the mounted controller so module-level recovery can ask for a root reconcile
 * without reaching into the React tree. Undefined while the controller is unmounted.
 */
let requestRootReconcile: (() => void) | undefined;

/** Whether the native side still has a component to answer for. */
async function hasNativeComponent(): Promise<boolean> {
  try {
    await NativeNavigation.get();
    return true;
  } catch {
    // `get` with no id answers for the top-most component, so the only thing it reports is
    // that there is nothing presented at all.
    return false;
  }
}

/**
 * Puts a root back up when the native side has lost the one this module thinks it is
 * driving. Returns true when it started a rebuild.
 *
 * A reflow drops the old tab webviews before installing the new ones, and a call landing in
 * that window can take the whole tabs component down with it. `tabsComponentId` then names
 * something native has never heard of, and because every later reflow is addressed to it —
 * the resume backstop in `onReturn` included — they all fail the same way and the screen
 * stays blank until the process is killed. This is the way out of that state.
 *
 * A swap already in flight is left alone: it raises a root of its own, and clearing the
 * tracked state underneath it would let two roots stack.
 */
export async function recoverLostNativeRoot(): Promise<boolean> {
  if (!isIosNativeNavigation() || isRootSwapInFlight()) return false;
  if (await hasNativeComponent()) return false;
  // A second look, because a rebuild costs the user every stack they had pushed. A
  // transient failure does not survive the retry; having nothing presented does.
  await new Promise((resolve) => setTimeout(resolve, 250));
  if (await hasNativeComponent()) return false;

  // Tear down before rebuilding, so that a `get` which failed for some reason other than an
  // empty native side cannot end up with two roots stacked.
  await rebuildNativeRoot();
  return true;
}

/** Tears the root down and has `reconcile` raise it again from the flags. */
async function rebuildNativeRoot(): Promise<void> {
  await NativeNavigation.reset().catch(() => {});
  tabsComponentId = undefined;
  presentedTabIds = [];
  builtTabIds = [];
  presentedMapTab = false;
  builtMapTab = false;
  setPresentedSplit(false);
  // Reads as "nothing presented", which is both true and what makes `reconcile` rebuild.
  setPresentedNativeRoot(null);
  requestRootReconcile?.();
}

/** The view on screen before a rebuild, which `restoreViewAfterRebuild` opens again. */
let viewBeforeRebuild: { tab: TabId; path: string; title: string } | undefined;

/**
 * Turning the two-pane setting on or off makes each pane tab a split or a plain stack. That
 * cannot be a reflow: a split leads with the tab's own stack id, so the plugin refuses to
 * create one beside the other, and the bar stays as it was. The root is rebuilt instead,
 * keeping the tab on screen and the view it showed. Whatever was pushed beneath that view goes.
 */
async function rebuildForSplitChange(): Promise<void> {
  viewBeforeRebuild = undefined;
  try {
    const { component, stack } = await NativeNavigation.get();
    const stackId = isTrailingStackId(stack?.id) ? leadingStackIdOf(stack.id) : stack?.id;
    const tab = TABS.find((candidate) => candidate.stackId === stackId);
    if (tab && component?.type === "view" && component.path) {
      viewBeforeRebuild = { tab: tab.id, path: component.path, title: component.title ?? "" };
    }
  } catch {
    // Nothing on screen to keep.
  }
  await rebuildNativeRoot();
}

/** Opens the view `rebuildForSplitChange` kept, once the rebuilt bar is up. */
async function restoreViewAfterRebuild(): Promise<void> {
  const view = viewBeforeRebuild;
  viewBeforeRebuild = undefined;
  if (!view || !isTabOnBar(view.tab)) return;
  // A tab's root, or a split's empty trailing column, is what the rebuilt tab opens on anyway.
  const atRoot = view.path === TAB_BY_ID[view.tab].route || view.path.startsWith("/panes/");
  if (atRoot) await selectTab(view.tab);
  else await pushInTab(view.tab, view.path, view.title);
}

/**
 * Resume backstop. Rebuilds the root when it has been lost, since a reflow addressed to the
 * dead tabs component would fail exactly as the one that lost it did, and otherwise brings
 * the bar's tab set back in line with the flags.
 */
export async function resumeNativeTabs(): Promise<void> {
  if (await recoverLostNativeRoot()) return;
  await syncPresentedTabs();
}

/** The reflow currently in flight, so two of them never overlap. */
let reflowInFlight: Promise<void> | null = null;

/** Set by a caller that arrived mid-reflow, so the run is repeated once it has finished. */
let reflowRequested = false;

/**
 * Brings the bar's tabs in line with the flags. A tab that leaves loses its webviews.
 *
 * Serialised, because the native reflow creates each new tab's stack under a fixed id
 * (`team-stack`, `chat-stack`) and only records it as present once the new webviews are
 * ready. Two reflows in flight therefore race to create the same component, and the loser
 * throws `componentAlreadyExists` inside a detached native Task, where no `catch` here can
 * see it. A caller arriving mid-reflow joins the running one and asks it to run again, so
 * its change is picked up whether the running reflow re-reads the flags in time or not.
 */
export async function syncPresentedTabs(): Promise<void> {
  if (!isIosNativeNavigation() || !tabsComponentId) return;
  if (reflowInFlight) {
    // Joining is not enough on its own. The running reflow may already have read the flags
    // for the last time and be on its way out, and then this call's change would have
    // nothing left to act on it, so ask for one more run.
    reflowRequested = true;
    return reflowInFlight;
  }

  reflowRequested = false;
  // Published so navigation can wait the reflow out rather than land in the window where
  // the old tab webviews have been dropped and the new ones are not installed yet.
  setTabReflowInFlight(true);
  reflowInFlight = reflowPresentedTabs().finally(() => {
    reflowInFlight = null;
    // A chained re-run leaves the bar unsettled, so the flag stays up across it and is
    // cleared by whichever run turns out to be the last.
    if (reflowRequested) void syncPresentedTabs();
    else setTabReflowInFlight(false);
  });
  return reflowInFlight;
}

/**
 * Convergent rather than edge-triggered. The flags that gate Chat and Team are derived from
 * the same claims but can still arrive in separate store writes, and a change landing while
 * a reflow is in flight has no later transition to ride. Each pass re-reads them, and the
 * loop ends only once the bar matches.
 */
async function reflowPresentedTabs(): Promise<void> {
  for (;;) {
    if (!tabsComponentId) return;

    const tabs = presentedTabs(tabAvailability());
    const ids = tabs.map((tab) => tab.id);
    const split = wantsSplitTabs();
    const mapTab = wantsMapTab(ids, split);
    // A reflow drops webviews, so an unchanged set is left alone. Turning the two-pane setting
    // on or off rebuilds the whole root instead (see `rebuildForSplitChange`).
    if (sameTabs(ids, presentedTabIds) && split === presentedSplit && mapTab === presentedMapTab) return;
    if (split !== presentedSplit) {
      await rebuildForSplitChange();
      return;
    }

    const spec = await buildTabSpecs(tabs, split, mapTab);
    try {
      // The plugin keeps the selected tab across a reflow (it records taps from @eventr/native 3.6.1).
      await NativeNavigation.update({ id: tabsComponentId, update: { tabs: spec }, animated: true });
    } catch (error) {
      // `presentedTabIds` is deliberately left naming what is still on screen, so the next
      // flag change tries again, as does the resume backstop in `onReturn`.
      console.error("Native tab reflow failed", error);
      // Unless the failure took the tabs component with it, in which case there is nothing
      // left for either of those to reflow and the root has to be rebuilt instead.
      await recoverLostNativeRoot();
      return;
    }
    presentedTabIds = ids;
    presentedMapTab = mapTab;
    setPresentedSplit(split);
    await refreshSplitCollapsed();

    // The spec was built from the locks as they stood when the reflow started, and a lock
    // can land while it is in flight — the organisation swap that reflows the bar is the
    // same event that re-reads the plan. Re-stating them afterwards is the cheaper half of
    // the pair and settles whichever order the two arrived in.
    await syncTabs();
  }
}

/**
 * Pushes the chat tab's unread badge. Read from the zustand store, not
 * `useRocketChatContext`, since `NativeRootController` is mounted above
 * `RocketChatProvider`; `rocketChatProvider` mirrors the count into the store for this.
 * Cleared while chat is locked, matching `ChatNavBarItem`'s web badge.
 */
// The last badge each sync was asked for. A count that arrives before the tab bar exists
// (the chat unread count usually does, set once at start-up) was dropped by the early return
// and never resent, as nothing changed afterwards; presenting the tabs replays these.
let lastChatBadge: { hasChatFeature: boolean; unreadCount: number } | undefined;
let lastAccountBadge: number | undefined;

async function replayTabBadges(): Promise<void> {
  if (lastChatBadge) await syncChatBadge(lastChatBadge.hasChatFeature, lastChatBadge.unreadCount);
  if (lastAccountBadge !== undefined) await syncAccountBadge(lastAccountBadge);
}

async function syncChatBadge(hasChatFeature: boolean, unreadCount: number): Promise<void> {
  lastChatBadge = { hasChatFeature, unreadCount };
  if (!isIosNativeNavigation() || !tabsComponentId) return;
  if (!isTabOnBar("chat")) return;
  await NativeNavigation.update({
    id: TAB_BY_ID.chat.stackId,
    update: { badgeValue: hasChatFeature && unreadCount > 0 ? String(unreadCount) : null },
  }).catch(() => {});
}

/** Pushes the account tab's badge: pending requests, matching `NavBar`'s `totalRequests`. */
async function syncAccountBadge(count: number): Promise<void> {
  lastAccountBadge = count;
  if (!isIosNativeNavigation() || !tabsComponentId) return;
  if (!isTabOnBar("account")) return;
  await NativeNavigation.update({
    id: TAB_BY_ID.account.stackId,
    update: { badgeValue: count > 0 ? String(count) : null },
  }).catch(() => {});
}

export function NativeRootController() {
  const hasToken = useBoundStore((s) => s.IDtoken !== undefined);
  const isGuestMode = useGuestStore((s) => s.isGuestMode);
  // Re-renders (and triggers a reconcile) when the pending auth or guest link is set or cleared.
  useSyncExternalStore(subscribePendingLinks, pendingLinksSnapshot);
  // And when a screen takes or releases its hold on the auth root.
  useSyncExternalStore(subscribeNativeRootState, isAuthRootHeld);

  const desiredRoot: RootKind = rootFor(hasToken || isGuestMode);

  const desiredRef = useRef<RootKind>(desiredRoot);
  desiredRef.current = desiredRoot;

  const reconcile = useCallback(async () => {
    if (!isIosNativeNavigation() || isRootSwapInFlight()) return;
    setRootSwapInFlight(true);
    try {
      // Drain to the latest desired root: if auth flips again mid-transition, the
      // loop re-checks and applies the newest target rather than a stale one.
      while (presentedNativeRoot() !== desiredRef.current) {
        const target = desiredRef.current;
        const component = target === "tabs" ? await buildTabsSpec() : buildAuthSpec();
        // Set the interface style + tab bar tint before presenting so the new root inherits
        // them as it is raised, otherwise the liquid-glass tab bar flashes its default
        // appearance and corrects a frame later.
        await NativeNavigation.setInterfaceStyle({ style: nativeInterfaceStyle() });
        await NativeNavigation.setTabBarTint({ color: nativeTabBarTint() });
        const isInitialRootPresent = presentedNativeRoot() === null;
        if (!isInitialRootPresent) {
          // Tear the previous root all the way back to the Capacitor webview before
          // raising the new one, so roots never stack.
          await NativeNavigation.reset();
        }
        const result = await NativeNavigation.present({
          component,
          animated: !isInitialRootPresent,
          // The pre-auth shell must not be swipe-dismissable; the tab root can be.
          cancellable: target === "auth" ? false : undefined,
        });
        tabsComponentId = target === "tabs" ? result.id : undefined;
        // Only now is the built set actually on screen. The auth root has no tabs at all,
        // so it clears the record rather than leaving the previous session's set standing.
        presentedTabIds = target === "tabs" ? builtTabIds : [];
        presentedMapTab = target === "tabs" && builtMapTab;
        setPresentedSplit(target === "tabs" && builtSplit);
        setPresentedNativeRoot(target);
        if (presentedSplit) await refreshSplitCollapsed();
        await refreshVerticalBar();
        if (target === "tabs") {
          await replayTabBadges();
          await restoreViewAfterRebuild();
        }
      }
      // The flags can move while `present` is in flight, before there is a bar to reflow.
      if (presentedNativeRoot() === "tabs") await syncPresentedTabs();
    } catch (error) {
      console.error("Native root reconcile failed", error);
    } finally {
      setRootSwapInFlight(false);
    }
  }, []);

  useEffect(() => {
    void reconcile();
  }, [desiredRoot, reconcile]);

  // Lets `recoverLostNativeRoot` drive a rebuild: it runs outside the React tree, and the
  // reconcile is the only thing that knows how to raise a root.
  useEffect(() => {
    requestRootReconcile = () => void reconcile();
    return () => {
      requestRootReconcile = undefined;
    };
  }, [reconcile]);

  // The splits report collapsing and expanding, which decides two panes.
  useEffect(() => {
    listenForSplitChanges();
  }, []);

  // The vertical bar, which carries a screen's main action where there is one (see rail-actions).
  useEffect(() => {
    listenForVerticalBarChanges();
  }, []);

  // The bar only minimizes on scroll while the window is wide, so it follows the window turning.
  useEffect(() => {
    if (!isIosNativeNavigation()) return;
    const query = window.matchMedia(PORTRAIT_QUERY);
    const onChange = () => {
      if (!tabsComponentId) return;
      void NativeNavigation.update({
        id: tabsComponentId,
        update: { minimizeOnScroll: minimizesOnScroll() },
      }).catch(() => {});
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  // Every split shares one divider, which the plugin moves in all of them when one is dragged.
  // Kept where the web layout keeps its own, so the next launch builds the splits there.
  useEffect(() => {
    if (!isIosNativeNavigation()) return;
    const handle = NativeNavigation.addListener("splitResized", ({ primaryFraction }) =>
      writePaneSplit(primaryFraction),
    );
    return () => void handle.then((h) => h.remove()).catch(() => {});
  }, []);

  // The two-pane setting decides whether the pane tabs are splits.
  useEffect(() => {
    if (!isIosNativeNavigation()) return;
    return useTwoPaneSettingStore.subscribe((state, previous) => {
      if (state.twoPaneSetting !== previous.twoPaneSetting) void syncPresentedTabs();
    });
  }, []);

  // The Map tab is only on the bar while the splits show two panes (see `wantsMapTab`). A split
  // can report itself expanded and then collapsed as it is first laid out, and reflowing the bar
  // in between disturbs its collapse, so the bar follows only once the splits have settled.
  useEffect(() => {
    if (!isIosNativeNavigation()) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = useSplitStore.subscribe((state, previous) => {
      if (state.collapsed === previous.collapsed) return;
      clearTimeout(timer);
      timer = setTimeout(() => void syncPresentedTabs(), SPLIT_SETTLE_MS);
    });
    return () => {
      clearTimeout(timer);
      off();
    };
  }, []);

  // The plugin reports a refused tab selection through the bar buttons' click event.
  useEffect(() => {
    if (!isIosNativeNavigation()) return;
    const handle = NativeNavigation.addListener("click", ({ buttonId }) => {
      if (buttonId === MAP_TAB_SELECT_BUTTON_ID) {
        openHomeMap();
        return;
      }
      if (buttonId === HOME_SUMMARY_SELECT_BUTTON_ID) {
        void openHomeSummary();
        return;
      }
      if (buttonId === ACCOUNT_ROOT_SELECT_BUTTON_ID) {
        void resetToTabRoot("account");
        return;
      }
      const tabId = tabIdFromButtonId(buttonId);
      if (tabId) reportTabPress(tabId);
    });
    return () => void handle.then((h) => h.remove()).catch(() => {});
  }, []);

  // A tab becomes disabled long after the root was presented, and the whole bar is
  // suspended for the length of an organisation swap.
  useEffect(() => {
    if (!isIosNativeNavigation()) return;
    return useTabDisabledStore.subscribe(
      (state) => ({ tabs: state.tabs, suspended: state.suspended }),
      () => void syncTabs(),
      { equalityFn: shallow },
    );
  }, []);

  // The flags flip on selecting an organisation, without the root being re-presented.
  useEffect(() => {
    if (!isIosNativeNavigation()) return;
    return useBoundStore.subscribe(
      (state) => ({
        chatEnabled: state.chatEnabled,
        teamPageEnabled: state.teamPageEnabled,
        hasTeams: selectHasTeams(state),
      }),
      () => void syncPresentedTabs(),
      { equalityFn: shallow },
    );
  }, []);

  // Whether Home's tab hands its taps over follows its split showing two panes (see `homeTapsRefused`).
  useEffect(() => {
    if (!isIosNativeNavigation()) return;
    return useSplitStore.subscribe(() => {
      if (homeTapsRefused() !== homeSummaryStated) void syncTabs();
    });
  }, []);

  // A different itinerary clears what its tabs' trailing columns were showing of the last one. Not
  // the first selection, which has nothing open to clear, nor a refresh of the same one.
  useEffect(() => {
    if (!isIosNativeNavigation()) return;
    return useBoundStore.subscribe(
      (state) => state.selectedItinID,
      (itinId, previousItinId) => {
        if (previousItinId > 0 && itinId !== previousItinId) void resetItineraryTrailingColumns();
      },
    );
  }, []);

  // Guest mode changes the presented set.
  useEffect(() => {
    if (!isIosNativeNavigation()) return;
    return useGuestStore.subscribe(
      (state) => state.isGuestMode,
      () => void syncPresentedTabs(),
    );
  }, []);

  // Claims determine chat's Chat/Messages label (`tabLabel`); refresh it whenever they
  // change, e.g. a role change picked up on pull-to-refresh.
  useEffect(() => {
    if (!isIosNativeNavigation()) return;
    return useBoundStore.subscribe(
      (state) => state.claimsVersion,
      () => void syncTabs(),
    );
  }, []);

  // The chat badge tracks the unread count (mirrored into the store by
  // `rocketChatProvider`) and clears itself while the tier locks chat out.
  const hasChatFeature = useHasFeature("Chat");
  const chatUnreadCount = useBoundStore((s) => s.chatUnreadCount);
  useEffect(() => {
    void syncChatBadge(hasChatFeature, chatUnreadCount);
  }, [hasChatFeature, chatUnreadCount]);

  // The account badge counts pending access requests (owner/admin/manager roles only)
  // plus items needing profile info, mirroring `NavBar`'s `totalRequests`.
  const wpUser = useBoundStore((s) => s.wpUser);
  const accessRequests = useBoundStore((s) => s.accessRequests);
  const profileRequiredAdditionalInfo = useBoundStore((s) => s.profileRequiredAdditionalInfo);
  const hasAccessRequests =
    wpUser && wpUser.roles?.some((role) => ["administrator", "eventr_manager", "owner"].includes(role));
  const accountBadgeCount = hasAccessRequests
    ? accessRequests.length + (profileRequiredAdditionalInfo?.length ?? 0)
    : (profileRequiredAdditionalInfo?.length ?? 0);
  useEffect(() => {
    void syncAccountBadge(accountBadgeCount);
  }, [accountBadgeCount]);

  return null;
}

/**
 * Surface an open view for `path` by selecting its tab, so one screen means one WKWebView.
 * Returns true when it did, and the caller skips its push.
 *
 * Tab selection is the only surfacing mechanism the plugin offers, so anything out of its
 * reach returns false and pushes instead of leaving the tap dead.
 */
export async function surfacedExistingView(path: string): Promise<boolean> {
  if (!isIosNativeNavigation() || !nativeNavigationReact) return false;
  if (presentedNativeRoot() !== "tabs" || isRootSwapInFlight()) return false;
  if (isModalPresented()) return false;

  const routeNode = allRouteNodes.find((node) => matchPath(node.path, pathnameOf(path)));
  if (!routeNode || routeNode.meta.allowDuplicate || routeNode.meta.present) return false;

  const tab = routeNode.meta.tab;
  if (!tab) return false;

  const existing = Object.values(nativeNavigationReact.views()).find((view) => view.data.path === path);
  if (!existing) return false;

  // `get()` with no id answers for the top-most component. A throw would break every
  // push, so failure means "surface nothing".
  let top: AnyComponentModel | undefined;
  let currentStack: StackModel | undefined;
  try {
    ({ component: top, stack: currentStack } = await NativeNavigation.get());
  } catch {
    return false;
  }

  // Already on screen, so the push would be a no-op.
  if (top?.id === existing.id) return true;

  // Buried in the current stack. The plugin has no pop-to-view, so nothing can reveal it.
  if (existing.data.stack !== undefined && existing.data.stack === currentStack?.id) return false;

  await selectTab(tab);
  return true;
}
